import { Component, Input, forwardRef, OnInit, OnChanges, EventEmitter, Output } from '@angular/core';
import { Options } from 'ng5-slider';
import { FormGroup, ControlValueAccessor, NG_VALUE_ACCESSOR, FormControl } from '@angular/forms';
import { animate } from '@angular/animations';
import { INDRACurrencyService } from '@ibe.services/currency.indra.service';

@Component({
	selector: 'app-range-input',
	templateUrl: './range-input.indra.component.html',
	styleUrls: ['./range-input.component.scss'],
	providers: [
		{
			/**
			 * Custom Control value accessor
			 */
			provide: NG_VALUE_ACCESSOR,
			useExisting: forwardRef(() => INDRARangeInputComponent),
			multi: true
		}
	],
	host: {
		class: 'app-range-input'
	}
})
export class INDRARangeInputComponent implements OnInit, OnChanges, ControlValueAccessor {

	@Input() min;
	@Input() max;
	@Input() stepValue;
	
	@Input() availableSteps = [];
	@Input() maxSelectableStep = 18000;
	
	@Input() minLimit = 0;
	
	@Input() parentForm: FormGroup = null;
	@Input() formControlName = null;
	
	@Input() promoValue = null;
	@Input() hasPromo = false;

	@Input() initAnimate: boolean = true;
	
	/* from post sprint 9 merge
	@Input() discountUnit: string = 'M';

	@Input() ticksArray = [];
	@Input() showTicks = true;
	*/
	@Input() disabled: boolean = false;
	
	manualRefresh: EventEmitter<void> = new EventEmitter<void>();

	@Output() userChangeEnd = new EventEmitter();

	options: Options;
	currency: string;

	// The internal data model
	private innerValue = 0;

	// Placeholders for the callbacks which are later provided
	// by the Control Value Accessor
	private onTouchedCallback: () => {};
	private onChangeCallback: (_: any) => void = (_: any) => { };


	constructor(private currencyService: INDRACurrencyService) {
		this.currency = this.currencyService.INDRA_getCurrencySymbol().unit;
	}
	
	ngOnInit() {
		// this.INDRA_initializeSlider();
	}

	ngOnChanges() {
		this.INDRA_initializeSlider();
		this.manualRefresh.emit();
	}
	
	INDRA_initializeSlider() {
		this.options = {
			disabled: this.disabled,
			ariaLabel: this.INDRA_getSliderAriaLabel(),
			stepsArray: this.availableSteps,
			showTicks: true,
			showSelectionBar: true,
			autoHideLimitLabels: false,
			hideLimitLabels: true,
			minLimit: this.minLimit,
			translate: (value: number): string => {
				return `${this.currencyService.INDRA_formatMiles(value)} M` + '<div>' + this.INDRA_printDiscount(value) + '</div>';
			},
			ticksTooltip: (index: number): string => {
				return '<span class="bold">' + `${this.currencyService.INDRA_formatMiles(this.availableSteps[index].value)} M` + '</span><br>' + this.INDRA_printDiscount(this.availableSteps[index].value);
			}
		};
		const maxLimitIndex = this.availableSteps.findIndex(step => step.maxLimit);
		if (maxLimitIndex > -1) {
			this.options.maxLimit = maxLimitIndex;
		}
	}
	
	/* note: from post-sprint 8 merge
	TAP_initializeSlider() {
		this.options = {
			ariaLabel: this.TAP_getSliderAriaLabel(),
			showSelectionBar: true,
			autoHideLimitLabels: false,
			hideLimitLabels: true,
		};
		
		if (this.availableSteps.length > 0) {
			this.options.showTicks = true;
			this.options.stepsArray = this.availableSteps;
			this.options.minLimit = this.minLimit;
			this.options.translate = (value: number): string => {
				return `${value.toLocaleString('pt')} ${this.discountUnit}` + '<div>' + this.TAP_printDiscount(value) + '</div>';
			};
			this.options.ticksTooltip = (index: number): string => {
				return '<span class="bold">' + `${this.availableSteps[index].value.toLocaleString('pt')} ${this.discountUnit}` + '</span><br>' + this.TAP_printDiscount(this.availableSteps[index].value);
			};
		} else {
			this.options.floor = this.min;
			this.options.ceil = this.max;
			this.options.step = 0.01;
			this.options.translate = (value: number): string => {
				return `${value.toLocaleString('pt')} ${this.discountUnit}` + '<div>' + this.TAP_printPromoDiscount(value) + '</div>';
			};

		}

		if (this.ticksArray.length > 0)
			this.options.ticksArray = this.ticksArray;
	}
	
	TAP_printPromoDiscount(value) {
		return '-' + (value + 8).toLocaleString('pt', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
	}
	*/
	
	INDRA_printDiscount(value) {
		let discount = '';
		const STEP = this.INDRA_getStep(value);

		if (STEP !== undefined) {
			discount = '-' + this.currencyService.INDRA_formatCash(STEP.discount) + ' ' + this.currency;

			if(this.hasPromo && value === this.promoValue) {
				discount += '<br><div class="is-promo"></div>';
			}
		}

		return discount;
	}
	
	INDRA_getStep(value) {
		return this.availableSteps.find(step => {
			return step.value === value;
		});
	}
	
	INDRA_getSliderAriaLabel() {
		return 'Use left and right keyboard keys to select amount of miles';
		// return 'Use left and right keyboard keys to select amount of ' + (this.discountUnit === 'M' ? 'miles' : 'promos');
	}

	INDRA_onChange() {
		this.userChangeEnd.emit();
	}

	// get accessor
	get value(): any {
		return this.innerValue;
	}

	// set accessor including call the onchange callback
	set value(v: any) {
		if (v !== this.innerValue) {
			this.innerValue = this.min;
			this.onChangeCallback(v);
		} else {
			this.innerValue = this.min;
		}
	}

	// Set touched on blur
	onBlur() {
		this.onTouchedCallback();
	}

	// From ControlValueAccessor interface
	writeValue(value: any) {
		if (value !== this.innerValue) {
			this.innerValue = value;
		}
	}

	// From ControlValueAccessor interface
	registerOnChange(fn: any) {
		this.onChangeCallback = fn;
	}

	// From ControlValueAccessor interface
	registerOnTouched(fn: any) {
		this.onTouchedCallback = fn;
	}

}
